<template>
  <div class="textarea-field">
    <FieldLabel
      :id="fieldConfig && fieldConfig.id"
      :label="fieldConfig && fieldConfig.label"
      :description="fieldConfig && fieldConfig.description"
      :required="fieldConfig && fieldConfig.required"
    />

    <textarea
      :id="fieldConfig && fieldConfig.id"
      ref="input"
      class="field-input"
      :placeholder="fieldConfig && fieldConfig.placeholder"
      :disabled="fieldConfig && fieldConfig.disabled"
      :required="fieldConfig && fieldConfig.required"
      :maxlength="maxLengthProp"
      :aria-invalid="hasError ? 'true' : 'false'"
      rows="4"
      @input="onInput"
      @change="stopNativeChange"
    />

    <div v-if="showCounter" class="char-counter">
      {{ currentLength }}<span v-if="maxLengthProp">/{{ maxLengthProp }}</span>
    </div>

    <label
      v-if="hasError"
      :for="fieldConfig && fieldConfig.id"
      class="field-error"
    >
      {{ errorMessage }}
    </label>
  </div>
</template>

<script>
import FieldLabel from "../FieldLabel/FieldLabel.vue";
import errorDisplay from "../mixins/errorDisplay.js";
import nativeChangeGuard from "../mixins/nativeChangeGuard.js";
import vModelHelpers from "../mixins/vModelHelpers.js";

export default {
  name: "TextareaField",
  components: {
    FieldLabel,
  },
  mixins: [errorDisplay, nativeChangeGuard, vModelHelpers],
  props: {
    fieldConfig: {
      type: Object,
      default: () => ({
        id: "textarea-field",
        name: "",
        type: "textarea",
        label: "",
        placeholder: "",
        required: false,
        width: "FULL",
        disabled: false,
        minLength: 0,
        maxLength: undefined,
        showCounter: true,
      }),
    },
    value: {
      type: [String, Number],
      default: null,
    },
    modelValue: {
      type: [String, Number],
      default: undefined,
    },
  },
  data() {
    return {
      internalValue: this.initialFromModel(this.modelValue, this.value, ""),
      observer: null,
    };
  },
  computed: {
    maxLengthProp() {
      const maxLength = this.fieldConfig && this.fieldConfig.maxLength;
      return typeof maxLength === "number" ? maxLength : null;
    },
    showCounter() {
      const cfg = this.fieldConfig || {};
      return !!(cfg && cfg.showCounter);
    },
    currentLength() {
      const v = this.internalValue == null ? "" : String(this.internalValue);
      return v.length;
    },
  },
  watch: {
    value(newValue) {
      if (this.modelValue !== undefined) return;
      this.internalValue = newValue;
      if (this.$refs && this.$refs.input) {
        this.$refs.input.value = newValue == null ? "" : String(newValue);
        this.autoResize();
      }
    },
    modelValue(newValue) {
      this.internalValue = newValue;
      if (this.$refs && this.$refs.input) {
        this.$refs.input.value = newValue == null ? "" : String(newValue);
        this.autoResize();
      }
    },
  },
  mounted() {
    if (this.$refs && this.$refs.input) {
      const currentValue = this.internalValue;
      this.$refs.input.value = currentValue == null ? "" : String(currentValue);
      this.autoResize();
    }
  },
  methods: {
    onInput(event) {
      let inputValue = event && event.target ? event.target.value : event;
      const max = this.maxLengthProp;
      if (typeof max === "number") {
        const s = inputValue == null ? "" : String(inputValue);
        if (s.length > max) {
          inputValue = s.slice(0, max);
          if (this.$refs && this.$refs.input)
            this.$refs.input.value = inputValue;
        }
      }
      this.internalValue = inputValue;
      this.emitModel(inputValue);
      this.$nextTick(this.autoResize);
    },
    autoResize() {
      const el = this.$refs && this.$refs.input;
      if (!el) return;
      el.style.height = "auto";
      el.style.height = el.scrollHeight + "px";
    },
  },
};
</script>

<style lang="scss" scoped>
@import "../../../assets/scss/abstracts/mixins";
@import "../styles/field-shared";

.textarea-field {
  width: 100%;
  .field-input {
    resize: none;
    box-sizing: border-box;
  }
  .char-counter {
    margin-top: 4px;
    color: var(--cr-form-placeholder-color, #9ca3af);
    @include font-family("body");
    @include scale-fonts("form-label");
  }
}
</style>
